// Utilities for building enhanced form data snapshots, computing answered state,
// and clamping progress consistently across UI and persistence paths.
//
// Why this module exists
// ----------------------
// Historically we tried to increment/decrement a local counter to track answered questions.
// That approach is fragile because local UI state and Firestore can drift (rapid submits, edits,
// reloads, multi-device). The functions here enforce a single, deterministic way to compute
// progress: recompute from the canonical form structure (including the current submission) and
// clamp outputs to sane bounds. This makes behavior predictable, race-safe, and easy to test.
//
// Design choices
//  - Single source of truth: Always compute from a complete form snapshot rather than deltas
//  - Pure helpers: No side effects; callers can use them in save paths, UI, and backfill scripts
//  - Explicit clamping: Never allow answeredQuestions > totalQuestions or percentages > 100
//  - Skip semantics: A skipped question counts as "answered" for progress intentions
//  - Compatibility: Answers are recognized across multiple shapes (arrays, objects with otherValue, strings)
//  - Visibility-aware: When conditionsToShow rules are present on subquestions, only visible
//    subquestions count towards progress. A question whose follow-ups are all hidden (because
//    the gating answer excludes them) is considered answered as long as the gating subquestion
//    itself is answered. When follow-ups become visible again, ALL visible subquestions must
//    be answered for the question to count as complete.
//
// Performance notes
//  - buildEnhancedFormWithSubmission deep-clones with JSON stringify/parse for clarity and safety.
//    This is acceptable given our form sizes. If performance becomes a concern, we can replace it
//    with a targeted immutable update while preserving the same external API.

import { buildAnswerLookup, getVisibleSubquestionEntries, questionHasConditionalSubquestions } from './formVisibility';

/**
 * Build an enhanced, in-memory copy of the current form that includes the
 * just-submitted answers for the current question. This creates any missing
 * containers for panel/question and applies the skip flag for the current question.
 *
 * @param {object} existingFormData - The existing Firestore formData[formKey]
 * @param {object} currentQuestion - The current question definition (with subquestions)
 * @param {object} updatedAnswers - Map of serviceUserVarName -> processed answer value
 * @param {object} formState - Form state containing currentPanel and currentQuestion indices
 * @param {boolean} isSkipping - Whether the current question was skipped in this submission
 * @returns {object} Enhanced form data including the latest submission
 */
export function buildEnhancedFormWithSubmission(existingFormData, currentQuestion, updatedAnswers, formState, isSkipping) {
  try {
    const base = (existingFormData && typeof existingFormData === 'object') ? existingFormData : {};
    const baseSubsections = (base.formSubsections && typeof base.formSubsections === 'object') ? base.formSubsections : {};

    const panelIndex = formState.currentPanel;
    const questionIndex = formState.currentQuestion;

    // Clone only the branches we need (immutable updates)
    const newSubsections = { ...baseSubsections };
    const prevPanel = newSubsections[panelIndex];
    const prevPanelQuestions = (prevPanel && typeof prevPanel.questions === 'object') ? prevPanel.questions : {};
    const newPanel = { ...(prevPanel || {}), questions: { ...prevPanelQuestions } };

    const prevQuestion = newPanel.questions[questionIndex];
    const prevQuestionSubqs = (prevQuestion && typeof prevQuestion.subquestions === 'object') ? prevQuestion.subquestions : {};
    const newQuestion = { ...(prevQuestion || {}), subquestions: { ...prevQuestionSubqs } };

    // Patch fresh answers for this question
    Object.entries(currentQuestion.subquestions || {}).forEach(([sqIdx, sq]) => {
      const varName = sq?.serviceUserVarName;
      if (varName !== undefined && updatedAnswers[varName] !== undefined) {
        newQuestion.subquestions[sqIdx] = { ...sq, answer: updatedAnswers[varName] };
      }
    });

    // Reflect skip state for current question
    newQuestion.wasSkipped = !!isSkipping;

    newPanel.questions[questionIndex] = newQuestion;
    newSubsections[panelIndex] = newPanel;

    return { ...base, formSubsections: newSubsections };
  } catch (err) {
    // Robust fallback: deep-clone and apply updates if the targeted merge fails
    console.error('[formProgress.buildEnhancedFormWithSubmission] targeted merge failed; falling back to deep clone', err);
    const enhanced = JSON.parse(JSON.stringify(existingFormData || { formSubsections: {} }));

    if (!enhanced.formSubsections[formState.currentPanel]) {
      enhanced.formSubsections[formState.currentPanel] = { questions: {} };
    }
    if (!enhanced.formSubsections[formState.currentPanel].questions[formState.currentQuestion]) {
      enhanced.formSubsections[formState.currentPanel].questions[formState.currentQuestion] = { subquestions: {} };
    }

    Object.entries(currentQuestion.subquestions || {}).forEach(([sqIdx, sq]) => {
      const varName = sq?.serviceUserVarName;
      if (varName !== undefined && updatedAnswers[varName] !== undefined) {
        enhanced.formSubsections[formState.currentPanel].questions[formState.currentQuestion].subquestions[sqIdx] = {
          ...sq,
          answer: updatedAnswers[varName]
        };
      }
    });

    enhanced.formSubsections[formState.currentPanel].questions[formState.currentQuestion].wasSkipped = !!isSkipping;
    return enhanced;
  }
}

/**
 * Determine whether a question should be counted as answered.
 *
 * Semantics depend on whether the question has conditional subquestions
 * (conditionsToShow on any subquestion):
 *
 *  - Conditional questions (e.g. alcohol, smoking): uses "every" — ALL visible
 *    subquestions must have substantive answers. This prevents marking a question
 *    complete when only the gating subquestion is answered but newly-revealed
 *    follow-ups are not.
 *
 *  - Non-conditional questions (e.g. name, weight, address): uses "some" — at
 *    least one subquestion must have a substantive answer. These questions may
 *    have genuinely optional fields (middleName, alternate units like kg vs lb)
 *    that patients intentionally leave blank.
 *
 *  - Legacy path (no visibleSubquestionEntries argument): also uses "some" for
 *    backward compatibility.
 *
 * Rules:
 *  - If wasSkipped === true, count as answered regardless.
 *  - Substantive value is: non-null/undefined, not "No answer given",
 *    arrays with length > 0, non-empty trimmed strings, or any other present value.
 *
 * @param {object} question - A question object with optional wasSkipped and subquestions
 * @param {object[]|undefined} visibleSubquestionEntries - Optional filtered visible subquestions
 * @returns {boolean}
 */
export function isQuestionAnswered(question, visibleSubquestionEntries) {
  if (question?.wasSkipped === true) return true;

  const entries = visibleSubquestionEntries || Object.values(question?.subquestions || {});

  // Use every() only for conditional questions where visible entries were provided.
  // Non-conditional questions (no conditionsToShow on any subquestion) revert to
  // some() because they may contain genuinely optional fields like middleName or
  // alternate-unit inputs (kg vs lb, cm vs ft/in).
  const useEvery = visibleSubquestionEntries
    && questionHasConditionalSubquestions(question);
  const checker = useEvery ? "every" : "some";

  return entries[checker]((sq) => {
    const val = sq?.answer;
    if (val === null || val === undefined) return false;
    if (val === "No answer given") return false;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'string') return val.trim() !== '';
    return true;
  });
}

/**
 * Clamp a percentage value to the [0, 100] range.
 * @param {number} value
 * @returns {number}
 */
export function clampPercentage(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

/**
 * Normalize a stored form-instance progress field to a 0–100 percentage.
 *
 * GenericFormPage persists progress as a 0–1 fraction (e.g. 1 = complete).
 * Demo seeding and some legacy paths store 0–100 directly (e.g. 100 = complete).
 * Callers should use this helper instead of blindly multiplying by 100.
 *
 * @param {number} raw - Value from formInstance.progress
 * @returns {number} Clamped integer percentage 0–100
 */
export function normalizeStoredProgressToPercentage(raw) {
  if (typeof raw !== 'number' || Number.isNaN(raw)) return 0;
  const asPercent = raw > 1 ? raw : raw * 100;
  return clampPercentage(Math.round(asPercent));
}

/**
 * Compute clamped total, answered and percentage values.
 * @param {number} total - raw total questions
 * @param {number} answered - raw answered questions
 * @returns {{ totalQuestions: number, answeredQuestions: number, progressPercentage: number }}
 */
export function clampProgress(total, answered) {
  const totalQuestions = Math.max(0, total | 0);
  const answeredQuestions = Math.min(totalQuestions, Math.max(0, answered | 0));
  const pct = totalQuestions === 0 ? 0 : (answeredQuestions / totalQuestions) * 100;
  const progressPercentage = clampPercentage(pct);
  return { totalQuestions, answeredQuestions, progressPercentage };
}

/**
 * Recompute progress from a full formData[formKey] snapshot.
 * Pure: no mutation, no side effects.
 *
 * Visibility-aware: builds a single answer lookup from all subquestions in the form,
 * then for each question determines which subquestions are visible based on their
 * conditionsToShow rules. Questions where all subquestions are hidden (e.g. a gating
 * answer of "Never" hides every follow-up and the gating subquestion itself has no
 * conditions) are still counted normally. A question only drops from the total when
 * getVisibleSubquestionEntries returns an empty array (meaning zero subquestions are
 * visible, which would only happen if the single gating subquestion itself had a
 * condition -- not the case in our current model).
 *
 * @param {object} formDataForKey - A single form's data: { formSubsections: { [panel]: { questions: { [q]: {...} } } } }
 * @returns {{ totalQuestions: number, answeredQuestions: number, progressPercentage: number }}
 */
export function recomputeProgress(formDataForKey) {
  let total = 0;
  let answered = 0;
  const formSubsections = formDataForKey?.formSubsections || {};

  // Build a single answer lookup for the entire form so visibility conditions
  // that reference other subquestions can be resolved efficiently.
  const answerLookup = buildAnswerLookup(formSubsections);

  Object.values(formSubsections).forEach((subsec) => {
    Object.values(subsec?.questions || {}).forEach((q) => {
      const visibleEntries = getVisibleSubquestionEntries(q, answerLookup);

      // If no subquestions are visible at all, skip this question entirely --
      // it should not count towards progress.
      if (visibleEntries.length === 0) return;

      total += 1;
      if (isQuestionAnswered(q, visibleEntries)) answered += 1;
    });
  });

  return clampProgress(total, answered);
}


